// 214 §2 [RULED 51] — WHICH OF THE MATTER'S OPPOSING PARTIES THIS COUNSEL STANDS FOR: a compact multi-select on a
// counsel's chip, RolePicker's shape one slot over (the chosen parties as small tags, a click opens a checkbox list
// of the matter's opponents; several counsel may share a party, one counsel may stand for several). Empty reads
// « + partie » (unlocked) or « sans partie » (locked). Its own component file because it holds state (rules of
// hooks — never the function-call shortcut).
/* `import React`: react/react-in-jsx-scope is counted per JSX element and this is the rule's own remedy (189 §E). */
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "../i18n";
import { RAD, FS, Z } from "../lib/tokens";
import type { TFn } from "../lib/taxonomyTypes";

export interface PartyOption {
  readonly id: number;
  readonly name: string;
}

interface Props {
  readonly partyIds: readonly number[];
  readonly parties: readonly PartyOption[];
  readonly onChange: React.Dispatch<number[]>;
  readonly disabled?: boolean;
}

export default function CounselPartyPicker({ partyIds, parties, onChange, disabled }: Props) {
  const { t } = useTranslation() as { t: TFn };
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  /* The outside click closes the list. The listener is registered ONCE and never depends on `open`: an effect
     that lists the state it sets AND has a cleanup is the shape brief 78 ruled out (scripts/lint/
     effect-sets-its-own-dep.js) — the in-flight work resolves against a dead closure. Closing a closed list is
     a no-op, so the listener may stand for the component's life. */
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) setOpen(false);
    };
    globalThis.document.addEventListener("mousedown", h);
    return () => {
      globalThis.document.removeEventListener("mousedown", h);
    };
  }, []);
  const cur = partyIds.filter((id) => parties.some((p) => p.id === id));
  const toggle = (id: number) => {
    onChange(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };
  const openProps = disabled
    ? {}
    : {
        onClick: () => {
          setOpen((v) => !v);
        },
        style: { cursor: "pointer" },
      };
  const openStyle = openProps.style ?? {};
  const nameOf = (id: number) => (parties.find((p) => p.id === id) ?? { name: String(id) }).name;
  return (
    <span ref={ref} data-counsel-parties="" style={{ position: "relative", display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
      {cur.length === 0 ? (
        <span
          {...openProps}
          title={disabled ? undefined : t("counsel.parties.add_tooltip", { fallback: "Tick the parties this counsel represents in this matter" })}
          style={{ fontSize: FS.s8, color: "var(--tm)", fontStyle: "italic", ...openStyle }}
        >
          {disabled ? t("counsel.parties.none", { fallback: "no party" }) : t("counsel.parties.add", { fallback: "+ party" })}
        </span>
      ) : (
        cur.map((id) => (
          <span
            key={id}
            {...openProps}
            data-counsel-party={String(id)}
            style={{ fontSize: FS.s8, background: "var(--bi)", color: "var(--ts)", borderRadius: RAD.r3, padding: "0 4px", lineHeight: "13px", ...openStyle }}
          >
            {nameOf(id)}
          </span>
        ))
      )}
      {open && !disabled && (
        <div
          data-counsel-parties-menu=""
          style={{ position: "absolute", top: "100%", left: 0, zIndex: Z.popoverBackdrop, minWidth: 180, background: "var(--bc)", border: "1px solid var(--br)", borderRadius: RAD.r6, boxShadow: "0 6px 20px rgba(0,0,0,.7)", padding: 4 }}
        >
          <div style={{ fontSize: FS.s9, color: "var(--tm)", textTransform: "uppercase", letterSpacing: 0.4, padding: "2px 6px" }}>
            {t("counsel.parties.label", { fallback: "Represents" })}
          </div>
          {parties.length === 0 && (
            <div style={{ padding: "4px 6px", fontSize: FS.s10, color: "var(--tm)", fontStyle: "italic" }}>
              {t("counsel.parties.no_opponents", { fallback: "No opposing party in this matter yet" })}
            </div>
          )}
          {parties.map((p) => (
            <label key={p.id} className="menu-item" style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={cur.includes(p.id)}
                onChange={() => {
                  toggle(p.id);
                }}
              />
              <span>{p.name}</span>
            </label>
          ))}
        </div>
      )}
    </span>
  );
}
